/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { GridTicks } from './GridTicks';
import { Rectangle } from 'pixi.js-legacy';
import { clamp, uninterpolate, interpolate } from 'utils/basic-utils';
import { snapPixel } from 'utils/ticket-utils';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';

export class Grid {
	public width: number;
	public height: number;

	public worldPosition: number[] = [0, 0];

	public hTicks: GridTicks;
	public xAxisScale = 1.0;
	public xAxisOffset = 0.0;
	public xAnchor = 0.5;

	public vTicks: GridTicks;
	public yAxisScale = 1.0;
	public yAxisOffset = 0.0;
	public yAnchor = 0.0;

	public xDirection = 1.0;
	public yDirection = 1.0;

	public graphics: FancyGraphics = new FancyGraphics();

	public parent: FancyContainer;

	public xMinRange = 0;
	public xMaxRange = 0;
	public yMinRange = 0;
	public yMaxRange = 0;

	private _xAnchorOffset = 0.0;
	private _yAnchorOffset = 0.0;

	constructor(width: number, height: number, parent: FancyContainer) {
		this.parent = parent;

		parent.addChild(this.graphics);

		this.resize(width, height);
	}

	public destroy(): void {
		this.graphics.destroy();
		if (this.hTicks) {
			this.hTicks.destroy();
		}
		if (this.vTicks) {
			this.vTicks.destroy();
		}

		this.hTicks = null;
		this.vTicks = null;
		this.graphics = null;
	}

	public resize(width: number, height: number): Grid {
		this.width = width;
		this.height = height;
		return this;
	}

	public setAnchor(x: number, y: number): Grid {
		this.xAnchor = clamp(x, -1, 1);
		this.yAnchor = clamp(y, -1, 1);
		return this;
	}

	public setScaleH(
		lods: number[],
		minScale: number,
		maxScale: number,
		minSpacing: number,
		maxSpacing: number
	): Grid {
		this.hTicks = new GridTicks().initTicks(lods, minScale, maxScale).spacing(minSpacing, maxSpacing);
		this.xAxisScale = clamp(this.xAxisScale, this.hTicks.minValueScale, this.hTicks.maxValueScale);
		return this;
	}

	public pixelToValueH(x: number): number {
		return (x - this.xAxisOffset) / this.xAxisScale;
	}

	public valueToPixelH(x: number): number {
		return x * this.xAxisScale + this.xAxisOffset;
	}

	public pixelToValueX(x: number) {}

	public setMappingH(minValue: number, maxValue: number, pixelRange: number): Grid {
		this._xAnchorOffset = minValue / (maxValue - minValue);
		this.xDirection = maxValue - minValue > 0 ? 1 : -1;

		this.pixelToValueX = function (x) {
			const pixelOffset = this.xAxisOffset;
			const ratio = this.width / pixelRange;
			const u = uninterpolate(0.0, this.width);
			const i = interpolate(minValue * ratio, maxValue * ratio);
			return i(u(x - pixelOffset)) / this.xAxisScale;
		};

		this.valueToPixelH = function (x) {
			const pixelOffset = this.xAxisOffset;

			const ratio = this.width / pixelRange;
			const u = uninterpolate(minValue * ratio, maxValue * ratio);
			const i = interpolate(0.0, this.width);
			return i(u(x * this.xAxisScale)) + pixelOffset;
		};

		return this;
	}

	public setRangeH(minValue: number, maxValue: number): Grid {
		this.xMinRange = minValue;
		this.xMaxRange = maxValue;
		return this;
	}

	public setScaleV(
		lods: number[],
		minScale: number,
		maxScale: number,
		minSpacing: number,
		maxSpacing: number
	): Grid {
		this.vTicks = new GridTicks().initTicks(lods, minScale, maxScale).spacing(minSpacing, maxSpacing);
		this.yAxisScale = clamp(this.yAxisScale, this.vTicks.minValueScale, this.vTicks.maxValueScale);
		return this;
	}

	public pixelToValueV(y: number): number {
		return (this.height - y + this.yAxisOffset) / this.yAxisScale;
	}

	public valueToPixelV(y: number): number {
		return y * this.yAxisScale + this.yAxisOffset;
		// return -y * this.yAxisScale + this.height + this.yAxisOffset;
	}

	public setMappingV(minValue: number, maxValue: number, pixelRange: number): Grid {
		this._yAnchorOffset = minValue / (maxValue - minValue);
		this.yDirection = maxValue - minValue > 0 ? 1 : -1;

		this.pixelToValueV = function (y) {
			const pixelOffset = this.yAxisOffset;
			const ratio = this.height / pixelRange;
			const u = uninterpolate(0.0, this.height);
			const i = interpolate(minValue * ratio, maxValue * ratio);
			return i(u(y - pixelOffset)) / this.yAxisScale;
		};

		this.valueToPixelV = function (y) {
			const pixelOffset = this.yAxisOffset;
			const ratio = this.height / pixelRange;
			const u = uninterpolate(minValue * ratio, maxValue * ratio);
			const i = interpolate(0.0, this.height);
			return i(u(y * this.yAxisScale)) + pixelOffset;
		};

		return this;
	}

	public setRangeV(minValue: number, maxValue: number): Grid {
		this.yMinRange = minValue;
		this.yMaxRange = maxValue;
		return this;
	}

	public xAxisScaleAt(pixelX: number, scale: number): Grid {
		const oldValueX = this.pixelToValueH(pixelX);
		this.xAxisScale = clamp(scale, this.hTicks.minValueScale, this.hTicks.maxValueScale);
		const newScreenX = this.valueToPixelH(oldValueX);
		this.pan(pixelX - newScreenX, 0);
		return this;
	}

	public yAxisScaleAt(pixelY: number, scale: number): Grid {
		const oldValueY = this.pixelToValueV(pixelY);
		this.yAxisScale = clamp(scale, this.vTicks.minValueScale, this.vTicks.maxValueScale);
		const newScreenY = this.valueToPixelV(oldValueY);
		this.pan(0, pixelY - newScreenY);
		return this;
	}

	public xAxisSync(x: number, scaleX: number): Grid {
		this.xAxisOffset = x;
		this.xAxisScale = scaleX;
		return this;
	}

	public yAxisSync(y: number, scaleY: number): Grid {
		this.yAxisOffset = y;
		this.yAxisScale = scaleY;
		return this;
	}

	public scaleAction(offsetX: number, offsetY: number, newScale: number): Grid {
		if (this.hTicks) {
			this.xAxisScaleAt(offsetX, newScale);
		}
		if (this.vTicks) {
			this.yAxisScaleAt(offsetY, newScale);
		}
		return this;
	}

	public draw(bounds: Rectangle, color: number = 0x000000, doNotClear: boolean = true): Grid {
		this.graphics.clear();
		this.drawHorizontal(bounds.left, bounds.right, color, doNotClear);
		this.drawVertical(bounds.top, bounds.bottom, color, doNotClear);
		return this;
	}

	public drawHorizontal(
		leftEdge: number,
		rightEdge: number,
		lineColor: number = 0x555555,
		doNotClear: boolean = true
	): Grid {
		if (this.hTicks) {
			const left = leftEdge;
			const right = rightEdge;

			if (!doNotClear) {
				this.graphics.clear();
			}

			this.graphics.beginFill(lineColor);
			this.hTicks.range(left, right, this.width);

			for (let i = this.hTicks.minTickLevel; i <= this.hTicks.maxTickLevel; ++i) {
				const ratio = this.hTicks.tickRatios[i];
				if (ratio > 0) {
					this.graphics.lineStyle(1, lineColor, ratio * 0.5);
					const ticks = this.hTicks.ticksAtLevel(i, true);
					for (let j = 0; j < ticks.length; ++j) {
						const screen_x = this.valueToPixelH(ticks[j]);
						this.graphics.moveTo(snapPixel(screen_x), -1.0);
						this.graphics.lineTo(snapPixel(screen_x), this.height);
					}
				}
			}
			this.graphics.endFill();
		}
		return this;
	}

	public drawVertical(
		topEdge: number,
		bottomEdge: number,
		lineColor: number = 0x555555,
		doNotClear: boolean = true
	): Grid {
		const top = topEdge;
		const bottom = bottomEdge;
		if (!doNotClear) {
			this.graphics.clear();
		}

		this.graphics.beginFill(lineColor);
		this.vTicks.range(top, bottom, this.height);
		for (let i = this.vTicks.minTickLevel; i <= this.vTicks.maxTickLevel; ++i) {
			const ratio = this.vTicks.tickRatios[i];
			if (ratio > 0) {
				this.graphics.lineStyle(1, lineColor, ratio * 0.5);
				const ticks = this.vTicks.ticksAtLevel(i, true);
				for (let j = 0; j < ticks.length; ++j) {
					const screen_y = this.valueToPixelV(ticks[j]);
					if (screen_y > bottom) {
						continue;
					}
					this.graphics.moveTo(0.0, snapPixel(screen_y));
					this.graphics.lineTo(this.width, snapPixel(screen_y));
				}
			}
		}
		this.graphics.endFill();

		return this;
	}

	public pan(deltaX: number, deltaY: number): Grid {
		this.panX(deltaX);
		this.panY(deltaY);
		return this;
	}

	public panX(deltaPixelX: number) {
		if (!this.valueToPixelH) {
			return;
		}

		const newOffset = this.xAxisOffset + deltaPixelX;
		this.xAxisOffset = 0.0; // calc range without offset

		let min, max;
		if (this.xMinRange !== undefined && this.xMinRange !== null) {
			min = this.valueToPixelH(this.xMinRange);
		}
		if (this.xMaxRange !== undefined && this.xMaxRange !== null) {
			max = this.valueToPixelH(this.xMaxRange);
			max = Math.max(0, max - this.width);
		}

		this.xAxisOffset = newOffset;

		if (min !== undefined && max !== undefined) {
			this.xAxisOffset = clamp(this.xAxisOffset, -max, -min);
			return;
		}

		if (min !== undefined) {
			this.xAxisOffset = Math.min(this.xAxisOffset, -min);
			return;
		}

		if (max !== undefined) {
			this.xAxisOffset = Math.max(this.xAxisOffset, -max);
		}
	}

	public panY(deltaPixelY: number) {
		if (!this.valueToPixelV) {
			return;
		}

		const newOffset = this.yAxisOffset + deltaPixelY;
		this.yAxisOffset = 0.0; // calc range without offset

		let min, max;
		if (this.yMinRange !== undefined && this.yMinRange !== null) {
			min = this.valueToPixelV(this.yMinRange);
		}
		if (this.yMaxRange !== undefined && this.yMaxRange !== null) {
			max = this.valueToPixelV(this.yMaxRange);
			max = Math.max(0, max - this.height);
		}

		this.yAxisOffset = newOffset;

		if (min !== undefined && max !== undefined) {
			this.yAxisOffset = clamp(this.yAxisOffset, -max, -min);
			return;
		}
		if (min !== undefined) {
			this.yAxisOffset = Math.min(this.yAxisOffset, -min);
			return;
		}

		if (max !== undefined) {
			this.yAxisOffset = Math.max(this.yAxisOffset, -max);
		}
	}
}
